import SectionHeader from "@/components/SectionHeader";
import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash, PencilSimple, FilePdf, Warning, ArrowsDownUp, Package, MagnifyingGlass, ShoppingCart, ShoppingCartSimple, CheckSquare, Square } from "@phosphor-icons/react";
import { api, apiError, downloadFile } from "@/lib/api";
import ProjectField, { assertProject, defaultProject } from "@/components/ProjectField";
import { usePermissions } from "@/lib/permissions";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import PurchaseOrdersTab from "./PurchaseOrdersTab";

const CATEGORIES = [
  { v: "explosivos", l: "Explosivos" },
  { v: "aceros", l: "Aceros / Fortificación" },
  { v: "epp", l: "EPP" },
  { v: "consumibles", l: "Consumibles" },
  { v: "combustibles", l: "Combustibles" },
  { v: "repuestos", l: "Repuestos" },
  { v: "quimicos", l: "Químicos" },
  { v: "herramientas", l: "Herramientas" },
  { v: "otros", l: "Otros" },
];
const CAT_LABEL = Object.fromEntries(CATEGORIES.map((c) => [c.v, c.l]));

const MOV_TYPES = [
  { v: "in", l: "Entrada" },
  { v: "out", l: "Salida" },
  { v: "adjust", l: "Ajuste" },
];
const MOV_LABEL = Object.fromEntries(MOV_TYPES.map((m) => [m.v, m.l]));

const emptyItem = () => ({
  code: "", description: "", category: "otros", unit: "und", project_id: defaultProject(),
  current_stock: 0, min_stock: 0, max_stock: 0,
  reorder_point: 0, lead_time_days: 7,
  unit_cost: 0,
  currency: "MXN", location: "", supplier: "", active: true, notes: "",
});

const emptyMov = (item) => ({
  item_id: item?.id || "",
  project_id: item?.project_id || defaultProject(),
  date: new Date().toISOString().slice(0, 10),
  type: "out", qty: 0, unit_cost: item?.unit_cost || 0,
  reference: "", area: "", operator_id: "", operator_name: "", notes: "",
});

function fmtMoney(n, cur = "MXN") {
  const v = Number(n || 0);
  return `${cur === "MXN" ? "MX$" : cur} ${v.toLocaleString("es-MX", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

// -------- Item form dialog --------
function ItemForm({ initial, onSaved, onCancel }) {
  const [f, setF] = useState(initial);
  const [busy, setBusy] = useState(false);
  const save = async () => {
    if (!f.code.trim() || !f.description.trim()) { toast.error("Código y descripción son obligatorios"); return; }
    if (!assertProject(f.project_id)) return;
    setBusy(true);
    try {
      if (f.id) await api.put(`/inventory/items/${f.id}`, f);
      else await api.post("/inventory/items", f);
      toast.success("Ítem guardado");
      onSaved();
    } catch (e) { toast.error(apiError(e)); }
    finally { setBusy(false); }
  };
  const upd = (k, v) => setF((s) => ({ ...s, [k]: v }));
  return (
    <div className="space-y-3">
      <div className="grid md:grid-cols-2 gap-3">
        <ProjectField className="md:col-span-2" value={f.project_id} onChange={(v) => upd("project_id", v)} testid="inv-item-project" />
        <div><Label className="overline-label">Código *</Label>
          <Input value={f.code} onChange={(e) => upd("code", e.target.value.toUpperCase())} data-testid="inv-item-code" /></div>
        <div><Label className="overline-label">Categoría</Label>
          <Select value={f.category} onValueChange={(v) => upd("category", v)}>
            <SelectTrigger data-testid="inv-item-category"><SelectValue /></SelectTrigger>
            <SelectContent>{CATEGORIES.map((c) => <SelectItem key={c.v} value={c.v}>{c.l}</SelectItem>)}</SelectContent>
          </Select></div>
      </div>
      <div><Label className="overline-label">Descripción *</Label>
        <Input value={f.description} onChange={(e) => upd("description", e.target.value)} data-testid="inv-item-desc" /></div>
      <div className="grid md:grid-cols-4 gap-3">
        <div><Label className="overline-label">Unidad</Label>
          <Input value={f.unit} onChange={(e) => upd("unit", e.target.value)} placeholder="und, kg, m, L" data-testid="inv-item-unit" /></div>
        <div><Label className="overline-label">Stock actual</Label>
          <Input type="number" step="0.01" value={f.current_stock} onChange={(e) => upd("current_stock", parseFloat(e.target.value) || 0)} data-testid="inv-item-stock" /></div>
        <div><Label className="overline-label">Stock mínimo</Label>
          <Input type="number" step="0.01" value={f.min_stock} onChange={(e) => upd("min_stock", parseFloat(e.target.value) || 0)} data-testid="inv-item-min" /></div>
        <div><Label className="overline-label">Stock máximo</Label>
          <Input type="number" step="0.01" value={f.max_stock} onChange={(e) => upd("max_stock", parseFloat(e.target.value) || 0)} /></div>
      </div>
      <div className="grid md:grid-cols-3 gap-3">
        <div><Label className="overline-label">Punto de reorden</Label>
          <Input type="number" step="0.01" value={f.reorder_point ?? 0}
                  onChange={(e) => upd("reorder_point", parseFloat(e.target.value) || 0)}
                  placeholder="Auto: mín × 1.2 si 0"
                  data-testid="inv-item-reorder" /></div>
        <div><Label className="overline-label">Lead time (días)</Label>
          <Input type="number" min="1" value={f.lead_time_days ?? 7}
                  onChange={(e) => upd("lead_time_days", parseInt(e.target.value) || 1)}
                  placeholder="Días de entrega del proveedor"
                  data-testid="inv-item-leadtime" /></div>
        <div className="text-[10px] text-slate-500 self-end pb-1">
          Cuando <b>stock ≤ reorder</b>, el ítem aparece en <b>Reposición</b> con sugerencias basadas en consumo × lead time.
        </div>
      </div>
      <div className="grid md:grid-cols-3 gap-3">
        <div><Label className="overline-label">Costo unitario</Label>
          <Input type="number" step="0.01" value={f.unit_cost} onChange={(e) => upd("unit_cost", parseFloat(e.target.value) || 0)} data-testid="inv-item-cost" /></div>
        <div><Label className="overline-label">Moneda</Label>
          <Select value={f.currency} onValueChange={(v) => upd("currency", v)}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="MXN">MXN</SelectItem>
              <SelectItem value="USD">USD</SelectItem>
              <SelectItem value="PEN">PEN</SelectItem>
            </SelectContent>
          </Select></div>
        <div><Label className="overline-label">Proveedor</Label>
          <Input value={f.supplier} onChange={(e) => upd("supplier", e.target.value)} /></div>
      </div>
      <div><Label className="overline-label">Ubicación / bodega</Label>
        <Input value={f.location} onChange={(e) => upd("location", e.target.value)} placeholder="Almacén A, Rampa -450, etc." data-testid="inv-item-location" /></div>
      <div><Label className="overline-label">Notas</Label>
        <Textarea rows={2} value={f.notes} onChange={(e) => upd("notes", e.target.value)} /></div>
      <div className="flex justify-end gap-2 pt-2">
        <Button variant="outline" onClick={onCancel}>Cancelar</Button>
        <Button onClick={save} disabled={busy} className="bg-blue-900 hover:bg-blue-800" data-testid="inv-item-save">
          {busy ? "Guardando…" : "Guardar"}
        </Button>
      </div>
    </div>
  );
}

// -------- Movement form dialog --------
function MovForm({ item, onSaved, onCancel }) {
  const [f, setF] = useState(emptyMov(item));
  const [busy, setBusy] = useState(false);
  const upd = (k, v) => setF((s) => ({ ...s, [k]: v }));
  const save = async () => {
    if (!f.qty || parseFloat(f.qty) === 0) { toast.error("La cantidad no puede ser cero"); return; }
    if (!assertProject(f.project_id)) return;
    setBusy(true);
    try {
      await api.post("/inventory/movements", { ...f, qty: parseFloat(f.qty), unit_cost: parseFloat(f.unit_cost) || 0 });
      toast.success("Movimiento registrado");
      onSaved();
    } catch (e) { toast.error(apiError(e)); }
    finally { setBusy(false); }
  };
  return (
    <div className="space-y-3">
      <div className="text-xs bg-slate-50 border border-slate-200 rounded p-2">
        <b>{item.code}</b> · {item.description}<br />
        <span className="text-slate-500">Stock actual: <b className="text-slate-800">{item.current_stock} {item.unit}</b></span>
      </div>
      <div className="grid md:grid-cols-3 gap-3">
        <ProjectField className="md:col-span-3" value={f.project_id} onChange={(v) => upd("project_id", v)} testid="inv-mov-project" />
        <div><Label className="overline-label">Tipo</Label>
          <Select value={f.type} onValueChange={(v) => upd("type", v)}>
            <SelectTrigger data-testid="inv-mov-type"><SelectValue /></SelectTrigger>
            <SelectContent>{MOV_TYPES.map((m) => <SelectItem key={m.v} value={m.v}>{m.l}</SelectItem>)}</SelectContent>
          </Select></div>
        <div><Label className="overline-label">Fecha</Label>
          <Input type="date" value={f.date} onChange={(e) => upd("date", e.target.value)} /></div>
        <div><Label className="overline-label">Cantidad ({item.unit})</Label>
          <Input type="number" step="0.01" value={f.qty} onChange={(e) => upd("qty", e.target.value)} data-testid="inv-mov-qty" /></div>
      </div>
      <div className="grid md:grid-cols-2 gap-3">
        <div><Label className="overline-label">Costo unitario</Label>
          <Input type="number" step="0.01" value={f.unit_cost} onChange={(e) => upd("unit_cost", e.target.value)} data-testid="inv-mov-cost" /></div>
        <div><Label className="overline-label">Referencia (BIT/OP/OT)</Label>
          <Input value={f.reference} onChange={(e) => upd("reference", e.target.value)} placeholder="BIT-xxxx" data-testid="inv-mov-ref" /></div>
      </div>
      <div className="grid md:grid-cols-2 gap-3">
        <div><Label className="overline-label">Área / zona</Label>
          <Input value={f.area} onChange={(e) => upd("area", e.target.value)} /></div>
        <div><Label className="overline-label">Operador</Label>
          <Input value={f.operator_name} onChange={(e) => upd("operator_name", e.target.value)} /></div>
      </div>
      <div><Label className="overline-label">Notas</Label>
        <Textarea rows={2} value={f.notes} onChange={(e) => upd("notes", e.target.value)} /></div>
      <div className="flex justify-end gap-2 pt-2">
        <Button variant="outline" onClick={onCancel}>Cancelar</Button>
        <Button onClick={save} disabled={busy} className="bg-blue-900 hover:bg-blue-800" data-testid="inv-mov-save">
          {busy ? "Guardando…" : "Registrar movimiento"}
        </Button>
      </div>
    </div>
  );
}

// -------- Reposición: sugerencias min/max/reorder point calculadas --------
function ReorderSuggestionsTab({ canWrite, onApplied }) {
  const [data, setData] = useState(null);
  const [category, setCategory] = useState("all");
  const [lookback, setLookback] = useState(90);
  const [busy, setBusy] = useState(false);
  const [applyingId, setApplyingId] = useState(null);
  const [selected, setSelected] = useState(() => new Set());
  const [poDialog, setPoDialog] = useState(false);
  const [poForm, setPoForm] = useState({
    supplier: "", supplier_rfc: "", supplier_contact: "",
    expected_delivery: "", notes: "",
  });
  const [poBusy, setPoBusy] = useState(false);

  const load = useCallback(async () => {
    setBusy(true);
    try {
      const params = { lookback_days: lookback };
      if (category !== "all") params.category = category;
      const r = await api.get("/inventory/reorder-suggestions", { params });
      setData(r.data);
      setSelected(new Set());
    } catch (e) { toast.error(apiError(e)); }
    finally { setBusy(false); }
  }, [lookback, category]);

  useEffect(() => { load(); }, [load]);

  const toggleSel = (id) => setSelected((prev) => {
    const n = new Set(prev);
    if (n.has(id)) n.delete(id); else n.add(id);
    return n;
  });
  const toggleAll = () => setSelected((prev) => {
    if (!data) return prev;
    if (prev.size === data.suggestions.length) return new Set();
    return new Set(data.suggestions.map((s) => s.item_id));
  });

  // Group selected items by supplier for the preview badge in the dialog
  const selectedRows = (data?.suggestions || []).filter((s) => selected.has(s.item_id));
  const selectedTotal = selectedRows.reduce((sum, s) => sum + (s.est_cost || 0), 0);
  const suppliers = [...new Set(selectedRows.map((s) => s.supplier).filter(Boolean))];

  const applySuggested = async (s) => {
    if (!window.confirm(
      `Aplicar mín ${s.suggested_min} · reorder ${s.suggested_reorder_point} · máx ${s.suggested_max} a ${s.item_code}?`
    )) return;
    setApplyingId(s.item_id);
    try {
      const rGet = await api.get("/inventory/items", { params: { q: s.item_code } });
      const item = (rGet.data || []).find((i) => i.id === s.item_id) || (rGet.data || [])[0];
      if (!item) throw new Error("Ítem no encontrado");
      await api.put(`/inventory/items/${s.item_id}`, {
        ...item,
        min_stock: s.suggested_min,
        max_stock: s.suggested_max,
        reorder_point: s.suggested_reorder_point,
      });
      toast.success(`Umbral actualizado en ${s.item_code}`);
      load();
      onApplied?.();
    } catch (e) { toast.error(apiError(e)); }
    finally { setApplyingId(null); }
  };

  const openPo = () => {
    if (selected.size === 0) { toast.error("Selecciona al menos un ítem"); return; }
    // Autofill supplier si todos los seleccionados comparten proveedor
    setPoForm((f) => ({
      ...f,
      supplier: suppliers.length === 1 ? suppliers[0] : f.supplier,
    }));
    setPoDialog(true);
  };

  const createPo = async () => {
    setPoBusy(true);
    try {
      const { data: po } = await api.post("/inventory/reorder-suggestions/create-po", {
        item_ids: Array.from(selected),
        supplier: poForm.supplier.trim(),
        supplier_rfc: poForm.supplier_rfc.trim(),
        supplier_contact: poForm.supplier_contact.trim(),
        expected_delivery: poForm.expected_delivery,
        notes: poForm.notes.trim(),
      });
      toast.success(`OC ${po.po_number} creada en borrador · ${fmtMoney(po.totals?.total || 0)}`);
      setPoDialog(false);
      setSelected(new Set());
      setPoForm({ supplier: "", supplier_rfc: "", supplier_contact: "", expected_delivery: "", notes: "" });
      window.dispatchEvent(new Event("inventory-updated"));
      load();
    } catch (e) { toast.error(apiError(e)); }
    finally { setPoBusy(false); }
  };

  return (
    <div className="space-y-3" data-testid="inv-reorder-tab">
      <div className="flex flex-wrap items-end gap-3">
        <div>
          <Label className="overline-label">Ventana de consumo (días)</Label>
          <Select value={String(lookback)} onValueChange={(v) => setLookback(parseInt(v))}>
            <SelectTrigger className="h-9 w-40" data-testid="inv-reorder-lookback"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="30">30 días</SelectItem>
              <SelectItem value="60">60 días</SelectItem>
              <SelectItem value="90">90 días</SelectItem>
              <SelectItem value="180">180 días</SelectItem>
              <SelectItem value="365">365 días</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="overline-label">Categoría</Label>
          <Select value={category} onValueChange={setCategory}>
            <SelectTrigger className="h-9 w-48" data-testid="inv-reorder-cat"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todas</SelectItem>
              {CATEGORIES.map((c) => <SelectItem key={c.v} value={c.v}>{c.l}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <Button variant="outline" onClick={load} disabled={busy} className="h-9"
                 data-testid="inv-reorder-refresh">
          {busy ? "Calculando…" : "Recalcular"}
        </Button>
        {canWrite && data && data.suggestions.length > 0 && (
          <Button onClick={openPo} disabled={selected.size === 0}
                   className="h-9 bg-orange-600 hover:bg-orange-500 gap-1.5"
                   data-testid="inv-reorder-create-po">
            <ShoppingCartSimple size={14} weight="bold" />
            Crear OC ({selected.size})
          </Button>
        )}
        {data && (
          <div className="ml-auto text-right">
            <p className="overline-label">Costo total estimado</p>
            <p className="text-xl font-heading font-black text-blue-900" data-testid="inv-reorder-total">
              {fmtMoney(data.total_est_cost)}
            </p>
          </div>
        )}
      </div>

      {!data ? (
        <p className="text-sm text-slate-400 italic text-center py-8">Cargando…</p>
      ) : data.suggestions.length === 0 ? (
        <p className="text-sm text-emerald-700 bg-emerald-50 border border-emerald-200 rounded p-4 text-center"
            data-testid="inv-reorder-empty">
          ✔ Ningún ítem alcanzó su punto de reorden. Todo por encima del umbral.
        </p>
      ) : (
        <div className="bg-white border border-slate-200 rounded-lg overflow-x-auto"
              data-testid="inv-reorder-table">
          <div className="bg-amber-50 border-b border-amber-200 px-4 py-2 flex items-center gap-2">
            <Warning size={16} weight="fill" className="text-amber-600" />
            <p className="text-sm font-bold text-amber-800">
              {data.total_items} ítem{data.total_items !== 1 ? "s" : ""} en o bajo su punto de reorden — sugerencias basadas en {data.lookback_days} días de consumo.
            </p>
            {selected.size > 0 && (
              <p className="ml-auto text-[11px] text-slate-700 font-bold" data-testid="inv-reorder-selected-badge">
                {selected.size} seleccionado{selected.size !== 1 ? "s" : ""} · {fmtMoney(selectedTotal)}
                {suppliers.length > 1 && <span className="ml-2 text-amber-700">· {suppliers.length} proveedores distintos</span>}
              </p>
            )}
          </div>
          <table className="w-full text-xs">
            <thead className="bg-slate-100 border-b border-slate-200">
              <tr className="text-left text-slate-500 uppercase tracking-wider">
                {canWrite && (
                  <th className="px-3 py-2 font-heading w-8">
                    <button type="button" onClick={toggleAll}
                             className="hover:text-blue-800 transition"
                             title={selected.size === data.suggestions.length ? "Deseleccionar todo" : "Seleccionar todo"}
                             data-testid="inv-reorder-toggle-all">
                      {selected.size === data.suggestions.length && data.suggestions.length > 0
                        ? <CheckSquare size={16} weight="fill" className="text-blue-800" />
                        : <Square size={16} weight="bold" />}
                    </button>
                  </th>
                )}
                <th className="px-3 py-2 font-heading">Código</th>
                <th className="px-3 py-2 font-heading">Descripción</th>
                <th className="px-3 py-2 font-heading">Proveedor</th>
                <th className="px-3 py-2 font-heading text-right">Stock</th>
                <th className="px-3 py-2 font-heading text-right">Mín · Reorden · Máx (actual)</th>
                <th className="px-3 py-2 font-heading text-right">Consumo/día</th>
                <th className="px-3 py-2 font-heading text-right">Días restantes</th>
                <th className="px-3 py-2 font-heading text-right">Mín · Reorden · Máx (sugerido)</th>
                <th className="px-3 py-2 font-heading text-right">Comprar</th>
                <th className="px-3 py-2 font-heading text-right">Costo est.</th>
                <th className="px-3 py-2 font-heading text-right">Acciones</th>
              </tr>
            </thead>
            <tbody>
              {data.suggestions.map((s) => {
                const dl = s.days_of_stock_left;
                const critical = s.criticality === "critical";
                const isSel = selected.has(s.item_id);
                return (
                  <tr key={s.item_id}
                       data-testid={`inv-reorder-row-${s.item_id}`}
                       className={`border-b border-slate-100 hover:bg-slate-50 ${critical ? "bg-red-50/40" : ""} ${isSel ? "bg-blue-50/50" : ""}`}>
                    {canWrite && (
                      <td className="px-3 py-2">
                        <button type="button" onClick={() => toggleSel(s.item_id)}
                                 className="hover:text-blue-800 transition"
                                 data-testid={`inv-reorder-select-${s.item_id}`}>
                          {isSel
                            ? <CheckSquare size={16} weight="fill" className="text-blue-800" />
                            : <Square size={16} weight="bold" />}
                        </button>
                      </td>
                    )}
                    <td className="px-3 py-2 font-mono font-bold text-slate-800">{s.item_code}</td>
                    <td className="px-3 py-2 text-slate-700">{s.description}</td>
                    <td className="px-3 py-2 text-slate-500 text-[11px]">{s.supplier || <span className="italic text-amber-600">Sin proveedor</span>}</td>
                    <td className={`px-3 py-2 text-right font-bold ${critical ? "text-red-700" : "text-slate-800"}`}>
                      {s.current_stock} {s.unit}
                    </td>
                    <td className="px-3 py-2 text-right text-slate-500 font-mono text-[11px]">
                      {s.min_stock} · {s.reorder_point} · {s.max_stock}
                    </td>
                    <td className="px-3 py-2 text-right font-mono">{s.avg_daily_consumption}</td>
                    <td className={`px-3 py-2 text-right font-bold ${dl !== null && dl < s.lead_time_days ? "text-red-700" : "text-slate-700"}`}>
                      {dl !== null ? `${dl} d` : "—"}
                    </td>
                    <td className="px-3 py-2 text-right font-mono text-[11px] text-blue-900">
                      <b>{s.suggested_min}</b> · {s.suggested_reorder_point} · {s.suggested_max}
                    </td>
                    <td className="px-3 py-2 text-right font-bold text-slate-800">{s.suggested_qty} {s.unit}</td>
                    <td className="px-3 py-2 text-right font-bold text-slate-800">{fmtMoney(s.est_cost)}</td>
                    <td className="px-3 py-2 text-right">
                      {canWrite && (
                        <Button size="sm" variant="outline" className="h-7 text-[11px]"
                                 disabled={applyingId === s.item_id || s.avg_daily_consumption <= 0}
                                 title={s.avg_daily_consumption > 0
                                   ? `Aplicar mín ${s.suggested_min} · reorden ${s.suggested_reorder_point} · máx ${s.suggested_max}`
                                   : "Sin consumo histórico — captura umbrales manualmente"}
                                 onClick={() => applySuggested(s)}
                                 data-testid={`inv-reorder-apply-${s.item_id}`}>
                          {applyingId === s.item_id ? "…" : "Aplicar"}
                        </Button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          <div className="border-t border-slate-200 bg-slate-50 px-4 py-2 text-[11px] text-slate-500">
            <b>Fórmulas:</b> Mín sug. = consumo diario × lead time × 1.3 · Reorden sug. = mín + 3 días de consumo · Máx sug. = mín × 3 · Cantidad a comprar = máx − stock actual.
          </div>
        </div>
      )}
    </div>
  );
}

// -------- Main page --------
export default function Inventory() {
  const { has } = usePermissions();
  const canWrite = has("inventory.write");
  const canPdf = has("inventory.reports");
  const [tab, setTab] = useState("catalog");
  const [items, setItems] = useState([]);
  const [movs, setMovs] = useState([]);
  const [summary, setSummary] = useState(null);
  const [q, setQ] = useState("");
  const [cat, setCat] = useState("all");
  const [showBelow, setShowBelow] = useState(false);
  const [itemDlg, setItemDlg] = useState(null);
  const [movDlg, setMovDlg] = useState(null);
  const [kardexItem, setKardexItem] = useState("all");

  const loadItems = useCallback(async () => {
    const params = new URLSearchParams();
    if (q) params.append("q", q);
    if (cat && cat !== "all") params.append("category", cat);
    if (showBelow) params.append("below_min", "true");
    const r = await api.get(`/inventory/items?${params.toString()}`);
    setItems(r.data || []);
  }, [q, cat, showBelow]);

  const loadMovs = useCallback(async () => {
    const params = new URLSearchParams();
    if (kardexItem && kardexItem !== "all") params.append("item_id", kardexItem);
    const r = await api.get(`/inventory/movements?${params.toString()}`);
    setMovs(r.data || []);
  }, [kardexItem]);

  const loadSummary = useCallback(async () => {
    const r = await api.get("/inventory/summary");
    setSummary(r.data);
  }, []);

  useEffect(() => { loadItems(); }, [loadItems]);
  useEffect(() => { loadMovs(); }, [loadMovs]);
  useEffect(() => { loadSummary(); }, [loadSummary]);

  const refreshAll = () => { loadItems(); loadMovs(); loadSummary(); };

  const removeItem = async (id) => {
    if (!window.confirm("¿Eliminar (o archivar si tiene movimientos) este ítem?")) return;
    try { await api.delete(`/inventory/items/${id}`); refreshAll(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const removeMov = async (id) => {
    if (!window.confirm("¿Anular este movimiento? El stock se revertirá.")) return;
    try { await api.delete(`/inventory/movements/${id}`); refreshAll(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const downloadPdf = async () => {
    try {
      const res = await api.get("/inventory/pdf", { responseType: "blob" });
      downloadFile(res.data, `inventario-${new Date().toISOString().slice(0, 10)}.pdf`);
    } catch (e) { toast.error(apiError(e)); }
  };

  const belowMinItems = useMemo(() => items.filter((i) => (i.current_stock ?? 0) <= (i.min_stock ?? 0)), [items]);

  return (
    <div className="space-y-6">
      {/* Header */}
      <SectionHeader icon={Package} title="Materiales / Inventario" eyebrow="Catálogo · Kardex · Alertas de reposición" size="lg" testid="inv-header"
        right={
          <>
            {canPdf && (
              <Button variant="outline" onClick={downloadPdf} className="gap-1.5" data-testid="inv-pdf">
                <FilePdf size={14} weight="bold" /> PDF Inventario
              </Button>
            )}
            {canWrite && (
              <Button className="bg-slate-900 hover:bg-slate-800 text-white gap-1.5" onClick={() => setItemDlg({ initial: emptyItem() })} data-testid="inv-new-item">
                <Plus size={14} weight="bold" /> Nuevo ítem
              </Button>
            )}
          </>
        } />

      {/* KPI cards */}
      {summary && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="inv-kpis">
          <div className="bg-white border border-slate-200 rounded-lg p-3">
            <p className="overline-label">Ítems activos</p>
            <p className="text-2xl font-heading font-black text-slate-900">{summary.total_items}</p>
          </div>
          <div className="bg-white border border-slate-200 rounded-lg p-3">
            <p className="overline-label">Valor total inventario</p>
            <p className="text-2xl font-heading font-black text-blue-900">{fmtMoney(summary.total_value)}</p>
          </div>
          <div className={`border rounded-lg p-3 ${summary.below_min_count > 0 ? "bg-red-50 border-red-200" : "bg-white border-slate-200"}`}>
            <p className="overline-label">En / bajo mínimo</p>
            <p className={`text-2xl font-heading font-black ${summary.below_min_count > 0 ? "text-red-700" : "text-slate-900"}`}>
              {summary.below_min_count}
            </p>
          </div>
          <div className="bg-white border border-slate-200 rounded-lg p-3">
            <p className="overline-label">Movimientos 30 días</p>
            <p className="text-2xl font-heading font-black text-slate-900">{summary.movements_30d}</p>
          </div>
        </div>
      )}

      {/* Tabs */}
      <div className="flex gap-1 border-b border-slate-200 -mb-px">
        {[
          { v: "catalog", l: "Catálogo", tid: "inv-tab-catalog" },
          { v: "kardex", l: "Kardex", tid: "inv-tab-kardex" },
          { v: "alerts", l: `Alertas${belowMinItems.length ? ` (${belowMinItems.length})` : ""}`, tid: "inv-tab-alerts" },
          { v: "reorder", l: "Reposición", tid: "inv-tab-reorder" },
          { v: "purchases", l: "Compras / OC", tid: "inv-tab-purchases" },
        ].map((x) => (
          <button
            key={x.v}
            onClick={() => setTab(x.v)}
            data-testid={x.tid}
            className={`px-3 py-2 text-sm font-heading font-bold border-b-2 -mb-px ${tab === x.v ? "border-blue-900 text-blue-900" : "border-transparent text-slate-500 hover:text-slate-800"}`}>
            {x.l}
          </button>
        ))}
      </div>

      {/* ---- Catalog ---- */}
      {tab === "catalog" && (
        <div className="space-y-3">
          <div className="flex flex-wrap items-center gap-2">
            <div className="relative flex-1 min-w-[200px]">
              <MagnifyingGlass size={14} className="absolute left-2 top-2.5 text-slate-400" />
              <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Código o descripción" className="h-9 pl-7" data-testid="inv-search" />
            </div>
            <Select value={cat} onValueChange={setCat}>
              <SelectTrigger className="w-52 h-9" data-testid="inv-filter-cat"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todas las categorías</SelectItem>
                {CATEGORIES.map((c) => <SelectItem key={c.v} value={c.v}>{c.l}</SelectItem>)}
              </SelectContent>
            </Select>
            <Button variant={showBelow ? "default" : "outline"} onClick={() => setShowBelow((s) => !s)} className={`h-9 ${showBelow ? "bg-red-600 hover:bg-red-500" : ""}`} data-testid="inv-filter-below">
              <Warning size={14} className="mr-1" /> Bajo mínimo
            </Button>
          </div>
          <div className="bg-white border border-slate-200 rounded-lg overflow-x-auto">
            <table className="w-full text-xs">
              <thead className="bg-slate-100 border-b border-slate-200">
                <tr className="text-left text-slate-500 uppercase tracking-wider">
                  <th className="px-3 py-2 font-heading">Código</th>
                  <th className="px-3 py-2 font-heading">Descripción</th>
                  <th className="px-3 py-2 font-heading">Categoría</th>
                  <th className="px-3 py-2 font-heading text-right">Stock</th>
                  <th className="px-3 py-2 font-heading text-right">Mín.</th>
                  <th className="px-3 py-2 font-heading text-right">Costo u.</th>
                  <th className="px-3 py-2 font-heading text-right">Valor</th>
                  <th className="px-3 py-2 font-heading">Ubicación</th>
                  <th className="px-3 py-2 font-heading text-right">Acciones</th>
                </tr>
              </thead>
              <tbody>
                {items.length === 0 && <tr><td colSpan={9} className="text-center py-8 text-slate-400">Sin ítems registrados.</td></tr>}
                {items.map((i) => {
                  const low = (i.current_stock ?? 0) <= (i.min_stock ?? 0);
                  return (
                    <tr key={i.id} data-testid={`inv-item-row-${i.id}`} className={`border-b border-slate-100 hover:bg-slate-50 ${low ? "bg-red-50/40" : ""}`}>
                      <td className="px-3 py-2 font-mono font-bold text-slate-800">{i.code}</td>
                      <td className="px-3 py-2 text-slate-700">{i.description}</td>
                      <td className="px-3 py-2 text-slate-500">{CAT_LABEL[i.category] || i.category}</td>
                      <td className={`px-3 py-2 text-right font-bold ${low ? "text-red-700" : "text-slate-800"}`}>
                        {i.current_stock} {i.unit}
                        {low && <Warning size={12} weight="bold" className="inline ml-1 text-red-600" />}
                      </td>
                      <td className="px-3 py-2 text-right text-slate-500">{i.min_stock}</td>
                      <td className="px-3 py-2 text-right text-slate-600">{fmtMoney(i.unit_cost, i.currency)}</td>
                      <td className="px-3 py-2 text-right font-bold text-slate-800">{fmtMoney((i.current_stock || 0) * (i.unit_cost || 0), i.currency)}</td>
                      <td className="px-3 py-2 text-slate-500">{i.location || "-"}</td>
                      <td className="px-3 py-2 text-right">
                        <div className="flex items-center gap-2 justify-end">
                          {canWrite && (
                            <button title="Registrar movimiento" onClick={() => setMovDlg(i)} className="text-blue-800 hover:text-blue-950" data-testid={`inv-mov-btn-${i.id}`}>
                              <ArrowsDownUp size={15} weight="bold" />
                            </button>
                          )}
                          {canWrite && (
                            <button title="Editar" onClick={() => setItemDlg({ initial: i })} className="text-slate-400 hover:text-blue-900" data-testid={`inv-edit-${i.id}`}>
                              <PencilSimple size={15} />
                            </button>
                          )}
                          {canWrite && (
                            <button title="Eliminar" onClick={() => removeItem(i.id)} className="text-slate-400 hover:text-red-600" data-testid={`inv-delete-${i.id}`}>
                              <Trash size={15} />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ---- Kardex ---- */}
      {tab === "kardex" && (
        <div className="space-y-3">
          <div className="flex flex-wrap items-center gap-2">
            <Label className="overline-label">Filtrar por ítem:</Label>
            <Select value={kardexItem} onValueChange={setKardexItem}>
              <SelectTrigger className="w-72 h-9" data-testid="inv-kardex-filter"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos los ítems</SelectItem>
                {items.map((i) => <SelectItem key={i.id} value={i.id}>{i.code} — {i.description}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="bg-white border border-slate-200 rounded-lg overflow-x-auto">
            <table className="w-full text-xs">
              <thead className="bg-slate-100 border-b border-slate-200">
                <tr className="text-left text-slate-500 uppercase tracking-wider">
                  <th className="px-3 py-2 font-heading">Fecha</th>
                  <th className="px-3 py-2 font-heading">Tipo</th>
                  <th className="px-3 py-2 font-heading">Ítem</th>
                  <th className="px-3 py-2 font-heading text-right">Cant.</th>
                  <th className="px-3 py-2 font-heading text-right">Costo u.</th>
                  <th className="px-3 py-2 font-heading text-right">Total</th>
                  <th className="px-3 py-2 font-heading text-right">Stock post.</th>
                  <th className="px-3 py-2 font-heading">Ref.</th>
                  <th className="px-3 py-2 font-heading">Área</th>
                  <th className="px-3 py-2 font-heading text-right">Acciones</th>
                </tr>
              </thead>
              <tbody>
                {movs.length === 0 && <tr><td colSpan={10} className="text-center py-8 text-slate-400">Sin movimientos.</td></tr>}
                {movs.map((m) => (
                  <tr key={m.id} data-testid={`inv-mov-row-${m.id}`} className="border-b border-slate-100 hover:bg-slate-50">
                    <td className="px-3 py-2 text-slate-600">{m.date}</td>
                    <td className="px-3 py-2">
                      <span className={`text-[10px] uppercase font-bold px-1.5 py-0.5 rounded ${m.type === "in" ? "bg-emerald-100 text-emerald-800" : m.type === "out" ? "bg-red-100 text-red-800" : "bg-amber-100 text-amber-800"}`}>
                        {MOV_LABEL[m.type]}
                      </span>
                    </td>
                    <td className="px-3 py-2"><b className="font-mono">{m.item_code}</b> · <span className="text-slate-500">{m.item_description}</span></td>
                    <td className={`px-3 py-2 text-right font-bold ${m.type === "in" ? "text-emerald-700" : m.type === "out" ? "text-red-700" : "text-amber-700"}`}>
                      {m.delta > 0 ? "+" : ""}{m.delta} {m.unit}
                    </td>
                    <td className="px-3 py-2 text-right text-slate-600">{fmtMoney(m.unit_cost, m.currency)}</td>
                    <td className="px-3 py-2 text-right text-slate-700">{fmtMoney(m.total, m.currency)}</td>
                    <td className="px-3 py-2 text-right font-bold text-slate-800">{m.stock_after} {m.unit}</td>
                    <td className="px-3 py-2 text-slate-500">{m.reference || "-"}</td>
                    <td className="px-3 py-2 text-slate-500">{m.area || "-"}</td>
                    <td className="px-3 py-2 text-right">
                      {canWrite && (
                        <button onClick={() => removeMov(m.id)} className="text-slate-400 hover:text-red-600" data-testid={`inv-mov-delete-${m.id}`} title="Anular">
                          <Trash size={14} />
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ---- Alerts ---- */}
      {tab === "alerts" && (
        <div className="space-y-3">
          {belowMinItems.length === 0 ? (
            <p className="text-sm text-emerald-700 bg-emerald-50 border border-emerald-200 rounded p-4 text-center" data-testid="inv-alerts-empty">
              ✔ Todos los ítems están sobre su stock mínimo.
            </p>
          ) : (
            <div className="bg-white border border-red-200 rounded-lg overflow-x-auto" data-testid="inv-alerts-table">
              <div className="bg-red-50 border-b border-red-200 px-4 py-2 flex items-center gap-2">
                <Warning size={16} weight="fill" className="text-red-600" />
                <p className="text-sm font-bold text-red-800">
                  {belowMinItems.length} ítem{belowMinItems.length !== 1 ? "s" : ""} necesita{belowMinItems.length === 1 ? "" : "n"} reposición
                </p>
              </div>
              <table className="w-full text-xs">
                <thead className="bg-slate-100 border-b border-slate-200">
                  <tr className="text-left text-slate-500 uppercase tracking-wider">
                    <th className="px-3 py-2 font-heading">Código</th>
                    <th className="px-3 py-2 font-heading">Descripción</th>
                    <th className="px-3 py-2 font-heading">Categoría</th>
                    <th className="px-3 py-2 font-heading text-right">Stock actual</th>
                    <th className="px-3 py-2 font-heading text-right">Mínimo</th>
                    <th className="px-3 py-2 font-heading text-right">Faltante</th>
                    <th className="px-3 py-2 font-heading">Proveedor</th>
                    <th className="px-3 py-2 font-heading text-right">Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {belowMinItems.map((i) => {
                    const deficit = Math.max((i.min_stock || 0) - (i.current_stock || 0), 0);
                    return (
                      <tr key={i.id} data-testid={`inv-alert-row-${i.id}`} className="border-b border-slate-100 hover:bg-slate-50">
                        <td className="px-3 py-2 font-mono font-bold">{i.code}</td>
                        <td className="px-3 py-2 text-slate-700">{i.description}</td>
                        <td className="px-3 py-2 text-slate-500">{CAT_LABEL[i.category] || i.category}</td>
                        <td className="px-3 py-2 text-right font-bold text-red-700">{i.current_stock} {i.unit}</td>
                        <td className="px-3 py-2 text-right text-slate-500">{i.min_stock} {i.unit}</td>
                        <td className="px-3 py-2 text-right font-bold text-red-800">{deficit} {i.unit}</td>
                        <td className="px-3 py-2 text-slate-500">{i.supplier || "-"}</td>
                        <td className="px-3 py-2 text-right">
                          {canWrite && (
                            <Button size="sm" variant="outline" className="h-7 text-[11px]" onClick={() => { setMovDlg(i); }} data-testid={`inv-alert-restock-${i.id}`}>
                              Reponer
                            </Button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          {summary?.top_consumed_30d?.length > 0 && (
            <div className="bg-white border border-slate-200 rounded-lg p-4">
              <h3 className="font-heading font-bold text-slate-900 mb-2">Top 5 consumidos (últimos 30 días)</h3>
              <ul className="space-y-1 text-xs">
                {summary.top_consumed_30d.map((t, idx) => (
                  <li key={t.item_id} data-testid={`inv-top-${idx}`} className="flex items-center justify-between border-b border-slate-100 pb-1">
                    <span><b className="font-mono">{t.code}</b> · {t.description}</span>
                    <span className="text-slate-600"><b>{t.qty_out} {t.unit}</b> · {fmtMoney(t.value_out)}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}

      {/* ---- Purchases (POs + Receptions + WAC/FIFO) ---- */}
      {tab === "purchases" && (
        <PurchaseOrdersTab
          items={items}
          canWrite={has("purchases.write")}
          canReports={has("purchases.reports")}
        />
      )}

      {/* ---- Reposición · sugerencias basadas en consumo ---- */}
      {tab === "reorder" && (
        <ReorderSuggestionsTab canWrite={canWrite} onApplied={refreshAll} />
      )}


      {/* Dialogs */}
      <Dialog open={!!itemDlg} onOpenChange={(o) => !o && setItemDlg(null)}>
        <DialogContent className="bg-white max-w-2xl max-h-[92vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-heading">{itemDlg?.initial?.id ? "Editar" : "Nuevo"} ítem</DialogTitle></DialogHeader>
          {itemDlg && (
            <ItemForm
              key={itemDlg.initial?.id || "new"}
              initial={itemDlg.initial}
              onSaved={() => { setItemDlg(null); refreshAll(); }}
              onCancel={() => setItemDlg(null)}
            />
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={!!movDlg} onOpenChange={(o) => !o && setMovDlg(null)}>
        <DialogContent className="bg-white max-w-xl max-h-[92vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-heading">Movimiento de stock</DialogTitle></DialogHeader>
          {movDlg && (
            <MovForm
              key={movDlg.id}
              item={movDlg}
              onSaved={() => { setMovDlg(null); refreshAll(); }}
              onCancel={() => setMovDlg(null)}
            />
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
